import { useMemo, useState } from "react";
import dayjs from "dayjs";
import useSWR from "swr";
import ReactApexChart from "react-apexcharts";
import { DatePickerInput } from "@mantine/dates";
import {
  Alert,
  Avatar,
  Box,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowUpRight,
  IconCalendarEvent,
  IconChecklist,
  IconClock,
  IconCoin,
  IconCrown,
  IconFileInvoice,
  IconGraph,
  IconRosetteDiscountCheck,
  IconTrendingUp,
  IconUsers,
  IconUsersGroup,
  IconX,
} from "@tabler/icons-react";
import { SalesAgentReport_Api, SalesReport_Api, USERCREATE_API } from "../../../api/endpoints";
import { fetcher } from "../../../services/fetcher";
import classes from "../components/LeadsOverview.module.css";

const CURRENCIES = ["INR", "NPR", "USD"];
const REV_COLORS = { INR: "#8B5CF6", NPR: "#FB923C", USD: "#3B82F6" };
const BAR_COLORS = ["#8B5CF6", "#FB923C", "#3B82F6", "#22C55E", "#EC4899", "#14B8A6", "#EAB308", "#6366F1", "#F43F5E"];
const STATUS_COLORS = {
  NEW: "#8B5CF6",
  CONTACTED: "#3B82F6",
  FOLLOW_UP: "#FB923C",
  PROPOSAL_SENT: "#EC4899",
  NEGOTIATION: "#22C55E",
  CLOSED_WON: "#4338CA",
  CLOSED_LOST: "#94A3B8",
};
const KPI_STYLES = [
  { bg: "#F3EEFF", color: "violet", icon: IconUsersGroup },
  { bg: "#EEFBF3", color: "green", icon: IconRosetteDiscountCheck },
  { bg: "#FFF1F2", color: "red", icon: IconX },
  { bg: "#EFF6FF", color: "blue", icon: IconGraph },
  { bg: "#FFF7ED", color: "orange", icon: IconFileInvoice },
  { bg: "#F5F3FF", color: "grape", icon: IconCalendarEvent },
  { bg: "#FFFBEB", color: "yellow", icon: IconCoin },
  { bg: "#EFF6FF", color: "blue", icon: IconUsers },
  { bg: "#FDF2F8", color: "pink", icon: IconCoin },
  { bg: "#ECFDF5", color: "green", icon: IconTrendingUp },
  { bg: "#FFF7ED", color: "orange", icon: IconChecklist },
  { bg: "#EFF6FF", color: "blue", icon: IconClock },
];
const nf = new Intl.NumberFormat("en-IN");
const chart = { fontFamily: "Inter, sans-serif", toolbar: { show: false } };
const panel = { radius: "md", withBorder: true, shadow: "xs", bg: "#fff", style: { borderColor: "#e8edf3" } };

const num = (v) => nf.format(Number(v || 0));
const labelize = (v) => String(v || "-").replace(/_/g, " ");
const list = (d) => (Array.isArray(d?.results) ? d.results : Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []);
const trend = (d) => (d == null ? null : `+${num(d)} vs previous`);
const capRange = (value, maxDay) => {
  if (!value?.[0]) return value;
  const clamp = (d) => (d && dayjs(d).isAfter(maxDay, "day") ? maxDay : d);
  const from = clamp(value[0]);
  let to = value[1] ? clamp(value[1]) : value[1];
  if (from && to && dayjs(from).isAfter(to, "day")) to = from;
  return [from, to];
};

function KpiCard({ item, style, loading }) {
  const Icon = style.icon;
  return (
    <Box className={classes.kpi} style={{ background: style.bg }}>
      <Group align="flex-start" wrap="nowrap" gap="sm">
        <Box className={classes.kpiIcon}>
          <ThemeIcon size={28} radius="md" variant="light" color={style.color}>
            <Icon size={16} />
          </ThemeIcon>
        </Box>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" c="#64748B" fw={500} lh={1.2}>{item.label}</Text>
          {item.currencies && !loading ? (
            <Stack gap={2}>
              {item.currencies.map(({ code, amount }) => (
                <Group key={code} gap={8} wrap="nowrap">
                  <Text size="10px" c="dimmed" w={26}>{code}</Text>
                  <Text size="xs" fw={700}>{amount}</Text>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text size="xl" fw={700} lh={1.1}>{loading ? "-" : item.value}</Text>
          )}
          {item.sub ? (
            <Group gap={4} wrap="nowrap">
              {!item.subMuted && <IconArrowUpRight size={14} color={item.subColor || "#16A34A"} />}
              <Text size="xs" c={item.subColor || "#16A34A"} fw={500}>{item.sub}</Text>
            </Group>
          ) : null}
        </Stack>
      </Group>
    </Box>
  );
}

const LeadsOverview = ({ onViewAllFollowups }) => {
  const today = useMemo(() => dayjs().startOf("day").toDate(), []);
  const [range, setRange] = useState(() =>
    capRange([new Date("2026-06-11"), new Date("2026-10-18")], dayjs().startOf("day").toDate())
  );
  const [leadType, setLeadType] = useState("all");
  const [agentId, setAgentId] = useState("all");
  const [start, end] = range;
  const params = useMemo(() => {
    const p = new URLSearchParams({
      date_from: dayjs(start).format("YYYY-MM-DD"),
      date_to: dayjs(end).format("YYYY-MM-DD"),
      scope: "both",
      lead_type: leadType === "all" ? "" : leadType,
    });
    return p.toString();
  }, [start, end, leadType]);

  const { data: teamReport, error: teamError, isLoading: tLoad } = useSWR(`${SalesReport_Api}?${params}`, fetcher, { revalidateOnFocus: false });
  const { data: agentReport, error: agentError, isLoading: aLoad } = useSWR(
    agentId === "all" ? null : `${SalesAgentReport_Api}${agentId}/?${params}`,
    fetcher,
    { revalidateOnFocus: false }
  );
  const { data: usersData, isLoading: uLoad } = useSWR(USERCREATE_API, fetcher, { revalidateOnFocus: false });

  const report = agentReport || teamReport;
  const loading = tLoad || aLoad;
  const period = report?.period || {};
  const current = report?.current || {};
  const breakdowns = report?.breakdowns || {};
  const delta = report?.deltas || {};
  const agents = useMemo(
    () =>
      list(usersData)
        .filter((r) => r?.user_profile_info?.user?.role === "Sales Team")
        .map((r) => {
          const u = r.user_profile_info.user;
          return { value: String(u.id), label: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email };
        }),
    [usersData]
  );

  const statusRows = (breakdowns.leads_by_status || [])
    .map((r) => ({
      label: labelize(r.status),
      value: Number(r.count || 0),
      color: STATUS_COLORS[r.status] || BAR_COLORS[0],
    }))
    .filter((r) => r.value);
  const statusTotal = statusRows.reduce((s, r) => s + r.value, 0) || Number(period.leads_created || 0);

  const bars = (rows, key) =>
    rows.map((r) => ({ label: r.label || labelize(r[key]), value: Number(r.count || 0) })).filter((r) => r.value);
  const sourceData = bars(breakdowns.leads_by_source || [], "source");
  const activityData = bars(breakdowns.activities || [], "activity_type");

  const money = (obj) => CURRENCIES.map((c) => ({ code: c, amount: num(obj?.[c] || 0) }));

  const metrics = [
    { label: "Leads Created", value: num(period.leads_created), sub: trend(delta.leads_created?.delta) },
    { label: "Closed Won", value: num(period.closed_won), sub: trend(delta.closed_won?.delta) },
    { label: "Closed Lost", value: num(period.closed_lost), sub: trend(delta.closed_lost?.delta), subColor: "#DC2626" },
    { label: "Conversion Rate", value: `${period.conversion_rate ?? 0}%`, sub: "vs previous —", subMuted: true },
    { label: "Quotations Sent", value: num(period.quotations_sent), sub: trend(delta.quotations_sent?.delta) },
    { label: "Bookings", value: num(period.bookings), sub: trend(delta.bookings?.delta) },
    { label: "Total Revenue", currencies: money(period.revenue) },
    { label: "Total Pax", value: num(period.pax), sub: trend(delta.pax?.delta) },
    { label: "Avg Deal Size", currencies: money(period.avg_deal_size) },
    { label: "Quote → Booking Rate", value: `${period.quote_to_booking_rate ?? 0}%` },
    { label: "Followups Done", value: `${num(period.followups_done)} / ${num(period.followups_due)}`, sub: `${period.followup_completion_rate ?? 0}% completion`, subMuted: true },
    { label: "Avg Response Time", value: `${period.avg_response_hours ?? 0} hours` },
  ];

  const fastest = current.fastest_response || report?.agent;
  const fastestName = fastest?.name || "-";

  const hBarOptions = (data) => ({
    chart,
    colors: data.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]),
    plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: "65%" } },
    dataLabels: { enabled: true, offsetX: 8, style: { fontSize: "11px", colors: ["#fff"] } },
    grid: { borderColor: "#eef2f7", strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    xaxis: { categories: data.map((d) => d.label), labels: { style: { fontSize: "11px", colors: "#64748B" } } },
    yaxis: { labels: { style: { fontSize: "11px" } } },
    legend: { show: false },
  });

  if (teamError || agentError) return <Alert color="red">Failed to load sales overview.</Alert>;

  return (
    <Box className={classes.shell}>
    <Stack gap="md">
        <Box className={classes.filterBar}>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            <DatePickerInput
              type="range"
              label="Selected Period"
              value={range}
              onChange={(value) => setRange(capRange(value, today))}
              valueFormat="DD MMM YYYY"
              allowSingleDateInRange
              maxDate={today}
            />
            <Select label="Lead Type" value={leadType} onChange={setLeadType} data={[{ value: "all", label: "All" }, { value: "B2B", label: "B2B" }, { value: "B2C", label: "B2C" }]} allowDeselect={false} />
            <Select label="Agent" value={agentId} onChange={setAgentId} searchable data={[{ value: "all", label: "All" }, ...agents]} allowDeselect={false} rightSection={loading || uLoad ? <Loader size="xs" /> : null} />
          </SimpleGrid>
        </Box>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 6 }} spacing="sm">
          {metrics.map((item, i) => <KpiCard key={item.label} item={item} style={KPI_STYLES[i]} loading={loading} />)}
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Paper {...panel} p="md">
            <Text fw={600} size="sm" mb="sm">Leads by Status</Text>
            <ReactApexChart
              type="donut"
              height={300}
              series={statusRows.map((r) => r.value)}
              options={{
                chart,
                labels: statusRows.map((r) => r.label),
                colors: statusRows.map((r) => r.color),
                dataLabels: { enabled: false },
                legend: {
                  position: "right",
                  fontSize: "11px",
                  formatter: (name, opts) => {
                    const v = opts.w.globals.series[opts.seriesIndex];
                    const pct = statusTotal ? ((v / statusTotal) * 100).toFixed(1) : 0;
                    return `${name}  ${v} (${pct}%)`;
                  },
                },
                plotOptions: {
                  pie: {
                    donut: {
                      size: "70%",
                      labels: {
                        show: true,
                        name: { show: true, fontSize: "11px", color: "#94A3B8", offsetY: 20 },
                        value: { show: true, fontSize: "22px", fontWeight: 700, offsetY: -8 },
                        total: { show: true, label: "Total Leads", fontSize: "11px", color: "#94A3B8", formatter: () => num(statusTotal) },
                      },
                    },
                  },
                },
              }}
            />
          </Paper>
          <Paper {...panel} p="md">
            <Text fw={600} size="sm" mb="sm">Leads by Source</Text>
            <ReactApexChart type="bar" height={Math.max(300, sourceData.length * 32)} series={[{ data: sourceData.map((d) => d.value) }]} options={hBarOptions(sourceData)} />
          </Paper>
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Paper {...panel} p="md">
            <Text fw={600} size="sm" mb="sm">Activities</Text>
            <ReactApexChart type="bar" height={280} series={[{ data: activityData.map((d) => d.value) }]} options={hBarOptions(activityData)} />
          </Paper>
          <Paper {...panel} p="md">
            <Group justify="space-between" mb="sm">
              <Text fw={600} size="sm">Revenue Overview</Text>
              <Group gap="md">
                {CURRENCIES.map((c) => (
                  <Group key={c} gap={6}>
                    <Box w={8} h={8} style={{ borderRadius: 4, background: REV_COLORS[c] }} />
                    <Text size="xs" c="dimmed">{c}</Text>
                  </Group>
                ))}
              </Group>
            </Group>
            <ReactApexChart
              type="bar"
              height={280}
              series={[{ name: "Revenue", data: CURRENCIES.map((c) => Number(period.revenue?.[c] || 0)) }]}
              options={{
                chart,
                colors: CURRENCIES.map((c) => REV_COLORS[c]),
                plotOptions: { bar: { distributed: true, columnWidth: "42%", borderRadius: 8 } },
                dataLabels: { enabled: true, formatter: (v) => num(v), offsetY: -20, style: { fontSize: "12px", fontWeight: 600 } },
                xaxis: { categories: CURRENCIES, labels: { style: { fontWeight: 600 } } },
                yaxis: { labels: { formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v) } },
                grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
                legend: { show: false },
              }}
            />
          </Paper>
        </SimpleGrid>

        <Group align="stretch" gap="md" wrap="nowrap" style={{ alignItems: "stretch" }}>
          <Paper {...panel} p="md" style={{ flex: "0 0 280px", maxWidth: 320 }}>
            <Group gap={6} mb="sm"><IconCrown size={16} color="#EAB308" /><Text size="sm" fw={600}>Fastest Response</Text></Group>
            <Group justify="space-between" wrap="nowrap" mt="md">
              <Group wrap="nowrap">
                <Avatar radius="xl" size={44}>{fastestName.charAt(0)}</Avatar>
                <Box>
                  <Text fw={600} size="sm">{fastestName}</Text>
                  <Text size="xs" c="dimmed">Agent ID: {fastest?.agent_id || fastest?.id || "-"}</Text>
                </Box>
              </Group>
            </Group>
            <Paper mt="md" p="sm" radius="md" bg="#FFF7ED" style={{ border: "1px solid #FFEDD5", textAlign: "center" }}>
              <Text fw={700} c="orange">{fastest?.avg_response_hours ?? report?.agent?.avg_response_hours ?? 0} hours</Text>
              <Text size="xs" c="dimmed">Avg Response Time</Text>
            </Paper>
          </Paper>
          <Paper {...panel} p="md" style={{ flex: 1, minWidth: 0 }}>
            <Group justify="space-between" mb="md">
              <Group gap={6}><IconTrendingUp size={16} /><Text fw={600} size="sm">Key Metrics <Text span c="dimmed" fw={400}>(vs Previous Period)</Text></Text></Group>
              {onViewAllFollowups ? <Text size="xs" c="blue" style={{ cursor: "pointer" }} onClick={onViewAllFollowups}>View followups</Text> : null}
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
              {["leads_created", "closed_won", "closed_lost", "quotations_sent", "bookings", "pax"].map((key) => (
                <Stack key={key} gap={4} align="center">
                  <Text size="xs" c="dimmed" ta="center">{labelize(key)}</Text>
                  <Text fw={700} size="lg" c="#16A34A">+{num(delta[key]?.delta)}</Text>
                  <Text size="10px" c="dimmed">Previous: {num(delta[key]?.previous)}</Text>
                </Stack>
              ))}
            </SimpleGrid>
          </Paper>
        </Group>
    </Stack>
    </Box>
  );
};

export default LeadsOverview;
